import * as React from "react";
import Button from "@mui/material/Button";
import CssBaseline from "@mui/material/CssBaseline";
import TextField from "@mui/material/TextField";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import MuiAlert from "@mui/material/Alert";
import { Paper, Snackbar } from "@mui/material";
import * as EmailValidator from "email-validator";
import { LoadingButton } from "@mui/lab";
import { sendResetOtp } from "../api/vendorAuthApi";

const defaultTheme = createTheme();

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="/">
        Shiftnpay, LLC, a Computerlog Company
      </Link>{" "}
      {new Date().getFullYear()}
      {"."}
    </Typography>
  );
}

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

export default function ForgotPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const emailFromState = location.state?.email || "";
  const [open, setOpen] = React.useState(false);
  const [customVariant, setCustomVariant] = React.useState("success");
  const [message, setMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [email, setEmail] = React.useState(emailFromState);
  const [emailError, setEmailError] = React.useState("");
  React.useEffect(() => {
    if (emailFromState) setEmail(emailFromState);
  }, [emailFromState]);

  const handleClose = (event, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    setEmailError("");
    if (!email) {
      setEmailError("Email is required");
      return;
    }
    if (!EmailValidator.validate(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }
    setLoading(true);
    try {
      await sendResetOtp(email);
      setMessage("OTP sent to your email. Please check your inbox.");
      setCustomVariant("success");
      setOpen(true);
      setTimeout(() => {
        navigate("/reset-password", { state: { email } });
      }, 1500);
    } catch (err) {
      setMessage(err?.response?.data?.msg || err?.response?.data?.message || "Failed to send OTP. Please try again.");
      setCustomVariant("error");
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={customVariant} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
      <Grid container component="main" sx={{ height: "100vh" }}>
        <CssBaseline />
        <Grid
          item
          xs={false}
          sm={4}
          md={7}
          sx={{
            backgroundImage: "url(https://shiftnpay.com/api/images/login/regisrationImage.png)",
            width: "100%",
            height: "auto",
            backgroundColor: (t) => (t.palette.mode === "light" ? t.palette.grey[50] : t.palette.grey[900]),
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "top !important",
          }}
        />
        <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
          <Box sx={{ my: 8, mx: 4, display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Box sx={{ width: 200, height: 150 }}>
              <img src="./logo/sky.png" alt="logo" style={{ width: "100%", height: "100%" }} />
            </Box>
            <Typography component="h1" variant="h5">
              Forgot Password
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: "center" }}>
              Enter your registered email and we&apos;ll send you an OTP to reset your password.
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 2, width: "100%" }}>
              <TextField
                error={!!emailError}
                helperText={emailError}
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                sx={{ mb: 2 }}
                autoComplete="email"
              />
              {loading ? (
                <LoadingButton loading fullWidth variant="contained" sx={{ mt: 2, mb: 2 }}>
                  Sending OTP
                </LoadingButton>
              ) : (
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2, bgcolor: "#108A00", "&:hover": { bgcolor: "#0D6E00" } }}
                >
                  Send OTP
                </Button>
              )}
              <Grid container justifyContent="center">
                <Grid item>
                  <Link to="/signin" variant="body2">
                    Back to Sign In
                  </Link>
                </Grid>
              </Grid>
              <Copyright sx={{ mt: 5 }} />
            </Box>
          </Box>
        </Grid>
      </Grid>
    </ThemeProvider>
  );
}
